import I18n from '@/modules/i18n';
import FE from 'froala-editor';
import {
  plato_api_program_path,
  shared_program_glossaries_path,
  shared_program_lesson_glossary_cards_path,
} from 'modules/routes';
import IGlossaryTerm from '@/components/interfaces/GlossaryTerm';
import Axios from 'axios';

const definedTerm = (term: IGlossaryTerm, locale: Locale = 'en') => {
  const termKey = locale === 'en' ? 'term_en' : 'term_es';
  let vocabTerm = term[termKey];

  if (locale === 'es' && vocabTerm.toLowerCase().includes('spanish placeholder')) {
    vocabTerm = term.term_en;
  }

  return vocabTerm;
};

const getDefinition = (term: IGlossaryTerm, locale: Locale = 'en') => {
  if (locale === 'en') return term.definition_en;

  if (locale === 'es' && !term.definition_es.toLowerCase().includes('spanish placeholder')) {
    return term.definition_es;
  }

  return term.definition_en;
};

const termsHtml = (terms: IGlossaryTerm[], locale: Locale = 'en') => (
  terms.map(term => `<p class="vocab-term">${definedTerm(term, locale)}</p>`).join('')
);

export const sortedTerms = (terms: IGlossaryTerm[], locale: Locale = 'en') => (
  terms
    .sort((a, b) => {
      const termA = definedTerm(a, locale);
      const termB = definedTerm(b, locale);

      return termA.toLowerCase().localeCompare(termB.toLowerCase());
    })
);

export async function fetchEdition(programId: string | number): Promise<string> {
  try {
    const res = await Axios.get(plato_api_program_path(programId));
    const isFourthEdition = res.data.program.edition === 'Fourth';
    const isElementary = ['K', '0', '1', '2', '3', '4', '5'].includes(res.data.program.grade.toString());

    return isFourthEdition && isElementary ? 'fourth-edition-elementary' : '';
  } catch (error) {
    console.error(error);

    return '';
  }
}

export const vocabBoxHtml = (
  edition: string,
  lessonId: number | string,
  locale: Locale,
  programId: number | string,
  terms: IGlossaryTerm[],
) => {
  const vocabLink = shared_program_lesson_glossary_cards_path(programId, lessonId);
  const glossaryLink = shared_program_glossaries_path(programId, { lesson_id: lessonId });

  return (`
    <div class="vocab-box ${edition}">
      <div class="vocab-box-header">
        <p class="vocab-title">Vocabulary</p>
        <div>
          <a id="dynamic-vocab-cards-link" class="vocab-btn" href="${vocabLink}" target="_blank">
            ${I18n.t('vocabulary_cards', { locale })}
          </a>
          <a id="dynamic-glossary" class="vocab-btn" href="${glossaryLink}" target="_blank">
            ${I18n.t('glossary', { locale })}
          </a>
        </div>
      </div>
      <div class="vocab-terms">
        ${termsHtml(sortedTerms(terms, locale), locale)}
      </div>
    </div>
  `);
};

const lessonOpenerCardImg = (term: IGlossaryTerm, locale: Locale) => {
  if (!term.s3_url) return '';
  if (term.s3_url.includes('missing')) return '';

  return `<img class="loc-img" alt="${definedTerm(term, locale)}" src="${term.s3_url}" />`;
};

const lessonOpenerCardHtml = (term: IGlossaryTerm, locale: Locale) => {
  const imgHtml = lessonOpenerCardImg(term, locale);
  const hasImg = imgHtml !== '';

  return (`
    <div class="loc ${hasImg ? 'with-img' : ''}">
      <div class="loc-header">
        <span class="heading2 loc-term">${definedTerm(term, locale)}</span>
        ${imgHtml}
      </div>
      <div class="lesson-opener-card-body">
        <span class="body lg loc-definition">${getDefinition(term, locale)}</span>
      </div>
    </div>
  `);
};

const lessonOpenerContent = (terms: IGlossaryTerm[], locale: Locale) => (
  sortedTerms(terms, locale).map(term => lessonOpenerCardHtml(term, locale)).join('\n')
);

export const lessonOpenerCardsHtml = (
  terms: IGlossaryTerm[],
  locale: Locale,
) => (`
  <div class="loc-grid" contenteditable="false">
    ${lessonOpenerContent(terms, locale)}
  </div>
`);

export const refreshTermsInEditor = (terms: IGlossaryTerm[], {
  selector = 'body',
  locale = 'en',
}: { selector: string, locale: Locale }) => {
  const termsContainer = document.querySelector(`${selector} .vocab-terms`);

  if (termsContainer) {
    termsContainer.innerHTML = termsHtml(terms, locale);
  }

  const lessonOpenerBox = document.querySelector(`${selector} .loc-grid`);

  if (lessonOpenerBox) {
    lessonOpenerBox.innerHTML = lessonOpenerContent(terms, locale);
  }
};

export const cleanUpDeleteButton = () => {
  const vocabBoxDeleteContainer = document.getElementById('vocabBoxDeleteContainer');
  if (vocabBoxDeleteContainer) vocabBoxDeleteContainer.remove();

  const vocabBoxDeleteButton = document.getElementById('vocabBoxDeleteButton');
  if (vocabBoxDeleteButton) vocabBoxDeleteButton.remove();
};
